import { supabase } from '../lib/supabase';

export interface ProcessoParticipo {
  participante_id: string;
  documento_id: string;
  numero: string;
  titulo: string;
  status: string;
  status_participante: 'convidado' | 'aceito' | 'recusado' | 'removido';
  modulo_codigo: string;
  acoes: string[];
  enviado_em: string | null;
  prazo_resposta: string | null;
  lido_em: string | null;
  respondido_em: string | null;
  ciencia_em: string | null;
}

export interface ParticipoAnexo {
  id: string;
  nome_original: string;
  tamanho_bytes: number;
  mime_type: string;
}

export interface ParticipoDetalhe {
  participante_id: string;
  nome: string;
  acoes: string[];
  status: string;
  prazo_resposta: string | null;
  solicitacao_texto: string | null;
  respondido_em: string | null;
  resposta_texto: string | null;
  ciencia_em: string | null;
  assinado_em: string | null;
  tem_anexo_proprio: boolean;
  documento: {
    numero: string;
    titulo: string;
    status: string;
    data_abertura: string;
    descricao: string | null;
    compartilha_corpo: boolean;
  };
  anexos: ParticipoAnexo[];
}

function checarErro<T>(data: T | { erro: string }): T {
  if (data && typeof data === 'object' && 'erro' in data) {
    throw new Error(String((data as { erro: string }).erro));
  }
  return data as T;
}

export const participacaoService = {
  async meusProcessos(): Promise<ProcessoParticipo[]> {
    const { data, error } = await supabase.rpc('participante_meus_processos');
    if (error) throw error;
    return (data ?? []) as ProcessoParticipo[];
  },

  async ver(participanteId: string): Promise<ParticipoDetalhe> {
    const { data, error } = await supabase.rpc('participante_ver', { p_participante_id: participanteId });
    if (error) throw error;
    return checarErro<ParticipoDetalhe>(data);
  },

  async responder(participanteId: string, texto: string): Promise<void> {
    const { data, error } = await supabase.rpc('participante_responder', { p_participante_id: participanteId, p_texto: texto });
    if (error) throw error;
    checarErro(data);
  },

  async darCiencia(participanteId: string): Promise<void> {
    const { data, error } = await supabase.rpc('participante_ciencia', { p_participante_id: participanteId });
    if (error) throw error;
    checarErro(data);
  },

  async responderConvite(participanteId: string, aceitar: boolean): Promise<void> {
    const { data, error } = await supabase.rpc('participante_responder_convite', { p_participante_id: participanteId, p_aceitar: aceitar });
    if (error) throw error;
    checarErro(data);
  },

  /** Anexa um documento ao processo (tarefa "anexar"). Sobe o arquivo na pasta do cidadão e registra. */
  async anexar(participanteId: string, arquivo: File): Promise<void> {
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData?.user?.id;
    if (!uid) throw new Error('Sessão expirada.');
    const ext = arquivo.name.split('.').pop() ?? 'bin';
    const nomeStorage = `cidadaos/${uid}/${crypto.randomUUID()}.${ext}`;
    const { error: erroUp } = await supabase.storage.from('documentos').upload(nomeStorage, arquivo, { contentType: arquivo.type });
    if (erroUp) throw erroUp;
    const { data, error } = await supabase.rpc('participante_anexar', {
      p_participante_id: participanteId,
      p_nome_storage: nomeStorage,
      p_nome_original: arquivo.name,
      p_mime: arquivo.type,
      p_tamanho: arquivo.size,
    });
    if (error) throw error;
    checarErro(data);
  },

  async assinar(participanteId: string, tipo: 'simples' | 'qualificada'): Promise<void> {
    const { data, error } = await supabase.rpc('participante_assinar', { p_participante_id: participanteId, p_tipo: tipo });
    if (error) throw error;
    checarErro(data);
  },

  /** URL assinada de download de um anexo compartilhado (valida sigilo na edge function). */
  async anexoUrl(participanteId: string, anexoId: string): Promise<string> {
    const { data, error } = await supabase.functions.invoke('participante-anexo-url', {
      body: { participante_id: participanteId, anexo_id: anexoId },
    });
    if (error) throw error;
    if (!data || (data as { erro?: string }).erro) {
      throw new Error((data as { erro?: string })?.erro ?? 'Anexo não disponível.');
    }
    return (data as { url: string }).url;
  },
};
